import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import { useTranslation } from 'react-i18next';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import IncomeExpenseSummary from '../../Summary/IncomeExpenseSummary';
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import Dropdown, { Option } from '../../../components/common/Dropdown/Dropdown';
import { exactMatcher, getFormErrorMessageByName, hasError, useFormErrorContext } from '../../../utils/context/FormErrorContext';
import { getProfileDetails, putPieAnalysis } from '../../../redux/actions/profile/profileDetails';
import { ProfileDetailsState } from '../../../redux/reducers/getProfile/getProfileDetailsReducer';
import "./Analysis.css"
import { selectELSLookups, selectPIEAnalysisCalculationData, selectProfileGetDetails, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths';
import { GetElsLookupsState } from '../../../redux/reducers/getLookups/getElsLookupsReducer';
import { useReadOnly } from '../../../utils/ReadOnlyContext';
import { initialPieAnalysis, keysOfPieAnalysisType, PieAnalysisType } from '../../../types/profileDetails/PieAnalysisType';
import { pickFromProfileDetailsType } from '../../../types/profileDetails/ProfileDetailsType';
import { useNavigation } from '../../../utils/context/NavigationContext';
import { getPIEAnalysisCalculationState } from '../../../redux/reducers/getPIEAnalysisCalculation/pieAnalysisCalculationReducer';
import { getPIEAnalysisCalculation } from '../../../redux/actions/analysis/analysis';
import ToolTip from '../../../components/common/ToolTip/ToolTip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faPercentage } from '@fortawesome/free-solid-svg-icons';
import { formatCurrency, formatPercentage } from '../../../utils/formatter';
import { EXTERNAL_SUBMITTED, INTERNAL_IN_PROGRESS } from '../../../redux/actionTypes/actionsTypes';
import i18n from '../../../i18n';
import Button from '../../../components/common/Button/Button';

type PieAnalysisCalculation = {
    income_attributes: Record<string, unknown>[] | null;
    income_attribute_lookups: Record<string, Record<string, Record<string, unknown>[]>> | null;
    pie_data: Record<string, Record<string, number>>;
    excluded_noi_income_attributes?: Record<string, Record<string, string[]>> | null;
    excluded_noi_profile_attributes?: Record<string, Record<string, string[]>> | null;
    total_noi?: number | null;
}

export default function Analysis(): JSX.Element {
    const dispatch = useDispatch();
    const { t } = useTranslation('pie');
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const profileType = profileOverview.profile.profile_type_code;
    const profileAttributeTranslationFile = profileType.toLowerCase() + "_profile_attribute";
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const incomeAttributeLookupType = profileType.toLowerCase()
    const currencySymbol = "$";
    const pieAnalysisCalculationState: getPIEAnalysisCalculationState = useSelector(selectPIEAnalysisCalculationData);
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const saveData: SaveDataState = useSelector(selectSaveData);
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const [pieAnalysisCalculation, setPIEAnalysisCalculation] = useState<PieAnalysisCalculation | null>(null);
    const [pieAnalysisData, setPieAnalysisData] = useState<PieAnalysisType>({ ...initialPieAnalysis });
    const getProfileDetailsState: ProfileDetailsState = useSelector(selectProfileGetDetails);
    const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string
    const propertyCode = sessionStorage.getItem("propertyCode") as string
    const { clearErrors, errors } = useFormErrorContext();
    const { isReadOnly } = useReadOnly()
    const [isFetchReqSent, setIsFetchReqSent] = useState(false);
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
    const campaignStatusCode = sessionStorage.getItem("campaignStatusCode") as string
    const { direction } = useNavigation()
    const [showPercentages, setShowPercentages] = useState(true);

    useEffect(() => {
        dispatch(getPIEAnalysisCalculation(profileType, currentSnapshotId))
        dispatch(getProfileDetails(currentSnapshotId))
        setIsFetchReqSent(true);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isFetchReqSent && (!getProfileDetailsState.loading && getProfileDetailsState.success)
            && (!pieAnalysisCalculationState.loading && pieAnalysisCalculationState.success)) {
            setPIEAnalysisCalculation(pieAnalysisCalculationState.data as PieAnalysisCalculation)
            setPieAnalysisData(pickFromProfileDetailsType(getProfileDetailsState.data, keysOfPieAnalysisType))
            if (getProfileDetailsState.data.analysisFlag === null) {
                setPieAnalysisData({ ...initialPieAnalysis })
            }
            setIsFetchReqSent(false);
        }
    }, [getProfileDetailsState, isFetchReqSent, pieAnalysisCalculationState]);

    useEffect(() => {
        if (saveResultState.status === 200) {
            dispatch(getProfileDetails(currentSnapshotId))
            setIsFetchReqSent(true);
        }
    }, [currentSnapshotId, dispatch, saveResultState.status]);

    useEffect(() => {
        if (saveData.saving) {
            dispatch(putPieAnalysis(currentSnapshotId, {
                "pie_analysis_data": pieAnalysisData,
                "spectral_section": profileType.toLowerCase() + "-campaign-analysis",
                "trigger_spectral": isInternal || direction !== "backward",
                "property_code": propertyCode,
                "is_internal": isInternal
            }))
        }
    }, [saveData.saving, profileType, pieAnalysisData, propertyCode, dispatch, currentSnapshotId, isReadOnly, t, isInternal, direction])

    const onAnalysisChange = (e: React.ChangeEvent) => {
        clearErrors();
        const target = e.target as HTMLInputElement | HTMLSelectElement;
        target.value = target.value.trim();
        const analysisReviewedBy = localStorage.getItem("username") ?? ""
        const analysisReviewedDate = (new Date()).toISOString()

        setPieAnalysisData({
            analysisFlag: target.value !== "" ? target.value : null,
            analysisReviewedBy: analysisReviewedBy,
            analysisReviewedDate: analysisReviewedDate
        })
    }

    const getTotalNOITooltip = () => {
        if (!pieAnalysisCalculation) {
            return null
        }

        const separator = " > "
        const sectionNavigationKeys: Record<string, string> = {
            "1": profileType === "LLE" ? "incomeRevenue" : "revenue",
            "2": "expenses",
            "4": profileType === "GLF" ? "GLF.reserve" : "reserveForReplacementAllowance"
        }

        const lis: string[] = []
        const excluded_noi_profile_attributes = pieAnalysisCalculation.excluded_noi_profile_attributes
        if (excluded_noi_profile_attributes) {
            Object.keys(excluded_noi_profile_attributes).forEach(section => {
                const sectionName = t(sectionNavigationKeys[section], { "ns": "navigation" }) + separator
                const excluded_noi_profile_attributes_section = excluded_noi_profile_attributes[section]
                const subsections = Object.keys(excluded_noi_profile_attributes_section)
                subsections.forEach(subsection => {
                    const subsectionName = t(subsection, { "ns": "navigation" }) + separator
                    const profileAttributeList = excluded_noi_profile_attributes_section[subsection]
                    profileAttributeList.forEach(profileAttribute => lis.push(sectionName + subsectionName + t("headers." + profileAttribute, { "ns": profileAttributeTranslationFile })))
                })
            })
        }
        const excluded_noi_income_attributes = pieAnalysisCalculation.excluded_noi_income_attributes
        const income_attributes = pieAnalysisCalculation.income_attributes
        const income_attribute_lookups = pieAnalysisCalculation.income_attribute_lookups
        if (excluded_noi_income_attributes && income_attributes && income_attribute_lookups) {
            Object.keys(excluded_noi_income_attributes).forEach(section => {
                const sectionName = t(sectionNavigationKeys[section], { "ns": "navigation" }) + separator
                const excluded_noi_income_attributes_section = excluded_noi_income_attributes[section]
                const subsections = Object.keys(excluded_noi_income_attributes_section)
                const subsectionsCount = subsections.length
                subsections.forEach(subsection => {
                    let subsectionName = ""
                    if (subsectionsCount > 1) {
                        subsectionName = t(subsection, { "ns": "navigation" }) + separator
                    }
                    const incomeAttributeCodeList = excluded_noi_income_attributes_section[subsection]
                    incomeAttributeCodeList.forEach(incomeAttributeCode => {
                        if (incomeAttributeCode !== "28") {
                            const attribute = income_attributes.find(attr => attr["code"] === incomeAttributeCode)
                            const lookup = income_attribute_lookups[section][subsection].find(lookup => lookup["incomeAttributeCode"] === incomeAttributeCode)
                            if (lookup) {
                                const userDescriptionPrioritized = lookup["userDescriptionPrioritized"] as boolean
                                if (userDescriptionPrioritized && attribute && attribute["description"]) {
                                    lis.push(sectionName + subsectionName + attribute["description"] as string)
                                } else {
                                    if (i18n.language === "fr") {
                                        lis.push(sectionName + subsectionName + lookup["incomeAttributeFrenchDescription"] as string)
                                    } else {
                                        lis.push(sectionName + subsectionName + lookup["incomeAttributeDescription"] as string)
                                    }
                                }
                            }
                        }
                    }
                    )
                })
            })
        }
        return <>
            <p>{t("analysis.total_noi_exclude_fields")}</p>
            <ul>
                {
                    lis.length > 0 &&
                    lis.map((li, key) => <li key={key}>{li}</li>)
                }
            </ul>
        </>

    }

    const getSectionData = (section: string) => pieAnalysisCalculation && pieAnalysisCalculation.pie_data ? pieAnalysisCalculation.pie_data[section] ?? {} : {};

    const buildSummaryData = (sectionData: Record<string, number>, totalKey: string, excludedKeys: string[] = []) => {
        const summaryData = Object.keys(sectionData).reduce<Record<string, number>>((result, key) => {
            if (key !== totalKey && !excludedKeys.includes(key)) {
                result[key] = sectionData[key];
            }
            return result;
        }, {});
        summaryData["grandTotal"] = sectionData[totalKey] ?? 0;
        return summaryData;
    }

    const calculateRoundedPercentages = (tableData: Record<string, number>, excludedKeys: string[] = []) => {
        const keys = Object.keys(tableData).filter((key) => key !== "grandTotal" && !excludedKeys.includes(key));
        let total = tableData.grandTotal;
        if (pieAnalysisCalculation && pieAnalysisCalculation.pie_data) {
            total = pieAnalysisCalculation.pie_data["1"]["totalRevenue"]
        }

        if (!keys.length || !total) {
            return {};
        }

        const percentages = keys.reduce<Record<string, number>>((result, key) => {
            result[key] = Number(((tableData[key] / total) * 100).toFixed(1));
            return result;
        }, {});

        // const roundedTotal = keys.reduce((sum, key) => sum + percentages[key], 0);
        // const roundingDifference = Number((100 - roundedTotal).toFixed(1));
        // const adjustmentKey = keys.reduce((lastKey, key) => tableData[key] > 0 ? key : lastKey, "");

        // if (adjustmentKey && percentages[adjustmentKey] + roundingDifference >= 0) {
        //     percentages[adjustmentKey] = Number((percentages[adjustmentKey] + roundingDifference).toFixed(1));
        // }

        return percentages;
    }

    const showPercentageControls = isInternal && [EXTERNAL_SUBMITTED, INTERNAL_IN_PROGRESS].includes(campaignStatusCode);
    const revenueSectionData = getSectionData("1");
    const expensesSectionData = getSectionData("2");
    const capitalExpendituresSectionData = getSectionData("3");
    const reserveSectionData = getSectionData("4");
    const { totalCapitalExpenditures, ...capitalExpendituresSubsections } = capitalExpendituresSectionData;
    const nonOperatingExpenseKeys = ["amortizationDepreciation", "nonOperatingIncomeExpenses", "GLF.nonOperatingIncomeExpenses"];
    const operatingExpenseExcludedKeys = ["replacementReserve", "reserve", ...nonOperatingExpenseKeys, "totalNonOperating"];
    const nonOperatingExpenseExcludedKeys = Object.keys(expensesSectionData).filter((key) => !nonOperatingExpenseKeys.includes(key));
    const totalRevenueData = buildSummaryData(revenueSectionData, "totalRevenue");
    const totalOperatingExpensesData = buildSummaryData(expensesSectionData, "totalOperating", operatingExpenseExcludedKeys);
    const totalNonOperatingExpensesData = buildSummaryData(expensesSectionData, "totalNonOperating", nonOperatingExpenseExcludedKeys);
    const totalCapitalExpendituresData = ["lle"].includes(incomeAttributeLookupType)
        ? capitalExpendituresSubsections
        : { grandTotal: totalCapitalExpenditures ?? 0 };
    const totalNetOperatingIncomeData = { grandTotal: Number(pieAnalysisCalculation?.total_noi ?? 0) };
    const totalReserveReplacementData = buildSummaryData(reserveSectionData, profileType === "GLF" ? "GLF.reserve" : "reserve");
    const totalNOITooltip = getTotalNOITooltip();
    const shouldShowPercentages = showPercentageControls && showPercentages;
    const totalRevenuePercentages = shouldShowPercentages ? calculateRoundedPercentages(totalRevenueData, ["rentalLoss"]) : undefined;
    const totalOperatingExpensesPercentages = shouldShowPercentages ? calculateRoundedPercentages(totalOperatingExpensesData) : undefined;

    return (
        <>
            <div className='summary-page-container analysis-page-container'>
                {pieAnalysisCalculationState.loading ? <></> : (pieAnalysisCalculationState.success === true ? <>
                    <p className='strong'>{t("analysis.desc")}</p>
                    {["bip", "htl", "mtl", "ltc", "rho", "lle", "glf"].includes(incomeAttributeLookupType) &&
                        pieAnalysisCalculation && pieAnalysisCalculation.pie_data && (
                            <ControlledAccordion providerValue={providerValue}>
                                <AccordionItem
                                    header={t("incomeExpense", { ns: 'summary' })}
                                    key="incomeExpense"
                                    itemKey="incomeExpense"
                                >
                                    <div className="accordion-controls">
                                        {showPercentageControls &&
                                            // <div className='airp-badge analysis-percent-toggle-container'>
                                            //     {/* <span className='analysis-percent-toggle-label'>
                                            //         {t("analysis.percentOfTotal")}
                                            //     </span> */}
                                            //     {/* <Switch
                                            //             id='analysis-percent-toggle'
                                            //             checked={showPercentages}
                                            //             onChange={(e) => setShowPercentages(e.target.checked)}
                                            //         /> */}
                                            // </div>
                                            <><ToolTip
                                                placement='left'
                                                contents={<Button onClick={() => setShowPercentages(!showPercentages)}
                                                    text={<FontAwesomeIcon icon={faPercentage} />}
                                                >
                                                </Button>}>
                                                {t("analysis.percentOfTotal")}
                                            </ToolTip>
                                            </>
                                        }
                                    </div>
                                    <div className='summary-grid'>
                                        <IncomeExpenseSummary
                                            currencySymbol={currencySymbol}
                                            totalHeader={t('revenues.totalRevenueIncome', { ns: 'pie' }) as string}
                                            tableData={totalRevenueData}
                                            percentageData={totalRevenuePercentages}
                                        />

                                        <IncomeExpenseSummary
                                            currencySymbol={currencySymbol}
                                            totalHeader={t('analysis.totalOperatingExpenses', { ns: 'pie' }) as string}
                                            tableData={totalOperatingExpensesData}
                                            percentageData={totalOperatingExpensesPercentages}
                                            headerPercent={showPercentages ? totalOperatingExpensesData.grandTotal / totalRevenueData.grandTotal * 100 : undefined}
                                        />

                                        <IncomeExpenseSummary
                                            currencySymbol={currencySymbol}
                                            totalHeader={t('analysis.totalNonOperatingExpenses', { ns: 'pie' }) as string}
                                            tableData={totalNonOperatingExpensesData}
                                        />
                                    </div>
                                    <div className='summary-grid'>
                                        {["htl", "mtl", "rho", "glf", "ltc"].includes(incomeAttributeLookupType) &&
                                            <IncomeExpenseSummary
                                                currencySymbol={currencySymbol}
                                                totalHeader={t('reserve.Total', { ns: 'pie' }) as string}
                                                tableData={totalReserveReplacementData}
                                            />
                                        }

                                        {["bip", "htl", "mtl", "rho", "lle", "glf"].includes(incomeAttributeLookupType) && (
                                            <IncomeExpenseSummary
                                                currencySymbol={currencySymbol}
                                                totalHeader={t('costs.totalCapitalExp', { ns: 'pie' }) as string}
                                                tableData={totalCapitalExpendituresData}
                                                separateTables={["lle"].includes(incomeAttributeLookupType)}
                                            />
                                        )}
                                    </div>
                                </AccordionItem>
                            </ControlledAccordion>
                        )
                    }

                    <div className="analysis-footer-row">
                        <div className="airp-badge badge-lighter-blue total-noi-container">
                            <span className='total-noi-label'>
                                {t('analysis.total', { ns: 'pie' })}
                                {totalNOITooltip &&
                                    <ToolTip tooltipClassName="fit-content-width-tooltip"
                                        contents={<FontAwesomeIcon icon={faCircleInfo} className="info-tooltip-icon" />}>
                                        {totalNOITooltip}
                                    </ToolTip>
                                }
                            </span>
                            <span className={`total-noi-value${Math.sign(totalNetOperatingIncomeData.grandTotal) === -1 ? " red-font" : ""}`}>
                                {currencySymbol + " " + formatCurrency(totalNetOperatingIncomeData.grandTotal, i18n.language)}
                                {showPercentages &&
                                    <>
                                        &nbsp;&nbsp;&nbsp;
                                        <span className='summary-percentage airp-badge badge-green'>{formatPercentage(totalNetOperatingIncomeData.grandTotal / totalRevenueData.grandTotal * 100)}</span>
                                    </>
                                }
                            </span>
                        </div>
                        <div className={`airp-badge badge-lighter-suburb-yellow analysis-container ${hasError(errors, "contact-analysis", exactMatcher) ? " table-error" : ""}`}>
                            <label className='contact-analysis-label' htmlFor='contact-analysis'>{t("analysis.analysis")}</label>
                            <Dropdown id='contact-analysis' name='analysisFlag'
                                options={(elsLookupsState.dropdowns["rentAnalysisOptions"] ?? []) as Option[]}
                                value={pieAnalysisData.analysisFlag ? pieAnalysisData.analysisFlag : ''}
                                errorMessage={getFormErrorMessageByName(errors, "contact-analysis", exactMatcher)}
                                hideErrorCross
                                onChange={onAnalysisChange} />
                        </div>
                    </div>
                </> : <></>)
                }
            </div >
        </>
    );
}
